/**
 * Темы оформления — §8.
 *
 * Меняется ТОЛЬКО акцентная пара: основной цвет и его тёмный вариант для
 * сайдбара, кнопок и активных состояний. Нейтральные цвета (фон, текст,
 * границы) и СТАТУСНЫЕ (готово / просрочено / оплачено) зафиксированы во всех
 * темах: зелёный означает «готово», красный — «проблема», и если бордовая тема
 * перекрасит их, пользователь перестанет читать список заказов взглядом.
 *
 * Реализовано через CSS-переменные, а не через мутацию объекта с цветами, как
 * было в демо (строка 5628): там смена темы правила модульную константу, что
 * не переживает разделение кода на чанки и не вызывает перерисовку React.
 */

import {
  FINANCE_COLOR_KEYS,
  STATUS_COLOR_KEYS,
  THEME_COLORS,
  resolveFinanceColors,
  resolveStatusColors,
  resolveThemePair,
  textOnColor,
  type ColorSettings,
  type FinanceColorKey,
  type StatusColorKey,
  type ThemeName,
  type ThemePair,
} from '@otc/shared';

export interface Theme {
  name: ThemeName;
  label: string;
  accent: string;
  accentDark: string;
}

/**
 * Названия тем — здесь, цвета — в общем наборе.
 *
 * Цвета нужны и серверу: он подставляет их в манифест приложения, откуда
 * Windows берёт цвет строки заголовка. Держать две таблицы значит однажды
 * открыть окно одним цветом, а шапку внутри увидеть другим.
 */
export const THEMES: Record<ThemeName, Theme> = {
  teal: { name: 'teal', label: 'Бирюзовая', ...THEME_COLORS.teal },
  blue: { name: 'blue', label: 'Синяя', ...THEME_COLORS.blue },
  gray: { name: 'gray', label: 'Серая', ...THEME_COLORS.gray },
  sage: { name: 'sage', label: 'Шалфейная', ...THEME_COLORS.sage },
  rose: { name: 'rose', label: 'Пыльная роза', ...THEME_COLORS.rose },
  plum: { name: 'plum', label: 'Сливовая', ...THEME_COLORS.plum },
  coffee: { name: 'coffee', label: 'Кофейная', ...THEME_COLORS.coffee },
  ink: { name: 'ink', label: 'Графитовая', ...THEME_COLORS.ink },
  custom: { name: 'custom', label: 'Своя', ...THEME_COLORS.custom },
};

/** Готовые темы для выбора. Своя рисуется отдельно — со своими полями цвета. */
export const THEME_LIST = Object.values(THEMES).filter((theme) => theme.name !== 'custom');

/** Применение темы: правится только акцентная пара. */
const THEME_KEY = 'otc.theme';

/*
 * Имя темы принимается СТРОКОЙ, а не только нынешним перечислением: из базы
 * может приехать прежнее название («зелёная», «бордовая»), и его надо перевести
 * в сегодняшнее, а не молча открыть программу бирюзовой.
 */
export function applyTheme(name: string, pair?: ThemePair | null): void {
  /* Своя тема берёт пару пользователя, готовая — из таблицы тем. */
  const theme = resolveThemePair(name, pair);
  const root = document.documentElement;
  root.style.setProperty('--otc-accent', theme.accent);
  root.style.setProperty('--otc-accent-dark', theme.accentDark);

  /*
   * Полоса состояния телефона красится в тот же цвет, что шапка программы.
   *
   * В установленном приложении она видна всегда и вплотную примыкает к шапке:
   * при расхождении сверху получается двухцветная ступенька. В разметке
   * страницы цвет задан бирюзовым — это лишь значение до загрузки темы.
   */
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme.accentDark);

  /*
   * Тема запоминается в браузере — не как настройка, а как ПОДСКАЗКА
   * на следующий запуск.
   *
   * Настоящая тема живёт на сервере и общая для лаборатории (§8), но
   * приезжает вместе со справочниками — через полсекунды после открытия.
   * До этого страница успевала отрисоваться бирюзовой, а потом на глазах
   * перекрашивалась в синюю. Подсказка позволяет взять верный цвет сразу.
   */
  try {
    /*
     * Сохраняются ЦВЕТА, а не название темы.
     *
     * Читает их `theme-boot.js` — отдельный файл, который выполняется до
     * первой отрисовки. Держи он у себя таблицу «название → цвета», она
     * существовала бы в двух местах и однажды разошлась бы с этой.
     */
    window.localStorage.setItem(
      THEME_KEY,
      JSON.stringify({ accent: theme.accent, accentDark: theme.accentDark }),
    );
  } catch {
    // Приватный режим: тема просто мигнёт, как раньше. Не повод падать.
  }
}


/**
 * Смысловые цвета статусов. Импортируются там, где цвет вычисляется в
 * TypeScript, а не задаётся классом Tailwind.
 *
 * ПЕРЕМЕННЫЕ, а не коды цвета: владелец настраивает оттенки в «Настройки →
 * Цвета», и перекраска должна дойти до каждой плашки без перезапуска и без
 * того, чтобы каждое место в программе подписывалось на настройки. Значение
 * переменной ставит applyColors, стандартные лежат в index.css.
 *
 * Поэтому к этим строкам нельзя дописывать прозрачность вида `${цвет}20` —
 * это уже не код цвета. Бледный тон делается через color-mix, как в Badge.
 *
 * Сами стандартные оттенки и то, как их подбирали, — DEFAULT_STATUS_COLORS
 * в @otc/shared.
 */
export const STATUS_COLORS: Record<StatusColorKey, string> = {
  /** Готово, оплачено, положительный доход. */
  done: 'var(--otc-status-done)',
  /** Очень срочно, просрочка. */
  critical: 'var(--otc-status-critical)',
  /** Срочно. */
  urgent: 'var(--otc-status-urgent)',
  /** В очереди, в работе. */
  normal: 'var(--otc-status-normal)',
  /**
   * Ожидание: заказ заморожен врачом.
   *
   * Синий выбран не случайно — в прежней таблице учёта такие строки красили
   * синим, и лаборатория читает этот цвет как «не трогаем, ждём».
   */
  hold: 'var(--otc-status-hold)',
  /** Ошибка, отказ, неотправленный счёт. */
  danger: 'var(--otc-status-danger)',
  /** Предупреждение: несохранённые правки, аванс, частичная оплата. */
  warning: 'var(--otc-status-warning)',
};


/** Подпись на заливке статусом — своя у каждого: на светлом цвете тёмная. */
export const STATUS_TEXT: Record<StatusColorKey, string> = Object.fromEntries(
  STATUS_COLOR_KEYS.map((key) => [key, `var(--otc-on-status-${key})`]),
) as Record<StatusColorKey, string>;

/**
 * Цвет подписи для заливки, заданной переменной статуса.
 *
 * Плашки получают цвет строкой `var(--otc-status-…)`, и сам код цвета им
 * неизвестен — узнать, светлый ли он, они не могут. Зато знают, какой это
 * статус: для него уже посчитана своя подпись. Всё прочее — белым, как было.
 */
export function textOn(color: string): string {
  const key = STATUS_COLOR_KEYS.find((k) => color === STATUS_COLORS[k]);
  return key ? STATUS_TEXT[key] : '#FFFFFF';
}

/** Цвета «Финансов»: разбор месяца и график. Настраиваются там же. */
export const FINANCE_COLORS: Record<FinanceColorKey, string> = Object.fromEntries(
  FINANCE_COLOR_KEYS.map((key) => [key, `var(--otc-fin-${key})`]),
) as Record<FinanceColorKey, string>;

const COLORS_KEY = 'otc.colors';

/**
 * Применение настроенных цветов: переменные на корне документа.
 *
 * Как и тема, цвета запоминаются в браузере ПОДСКАЗКОЙ на следующий запуск:
 * настоящие приезжают со справочниками через полсекунды, и без подсказки
 * «Ожидание» успевало бы мелькнуть стандартным синим, а потом перекраситься.
 * Читает подсказку theme-boot.js — поэтому там только готовые пары
 * «переменная → значение», без знания о статусах.
 */
export function applyColors(settings?: ColorSettings | null): void {
  const root = document.documentElement;
  const пары: [string, string][] = [];
  const status = resolveStatusColors(settings);
  for (const key of STATUS_COLOR_KEYS) {
    пары.push([`--otc-status-${key}`, status[key]]);
    пары.push([`--otc-on-status-${key}`, textOnColor(status[key])]);
  }
  const finance = resolveFinanceColors(settings);
  for (const key of FINANCE_COLOR_KEYS) пары.push([`--otc-fin-${key}`, finance[key]]);
  for (const [name, value] of пары) root.style.setProperty(name, value);

  try {
    window.localStorage.setItem(COLORS_KEY, JSON.stringify(Object.fromEntries(пары)));
  } catch {
    // Приватный режим: цвета просто мигнут при запуске. Не повод падать.
  }
}

